import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import { getGlobalUserRoleBadgeInfo } from '../utils/roleBadges';
import { useNotification } from '../contexts/NotificationContext';
import './UserManagement.css';

const UserManagement = () => {
  const { user, loading: authLoading, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddApiUserModal, setShowAddApiUserModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState({});
  const [userGroups, setUserGroups] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  // Perfil atual (normalizado) para regras de exibição por perfil
  const roleKey = String(user?.role || '').trim().toLowerCase();
  const isGerencialProfile = roleKey === 'gerencial' || roleKey === 'manager';
  const isTrialOrUnitarioProfile = roleKey === 'trial' || roleKey === 'unitario';
  
  // Estados para gerenciar roles
  const [roles, setRoles] = useState([]);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');

  // Lista de roles filtrada (memoizada)
  const filteredRoles = useMemo(() => {
    const term = (roleSearchTerm || '').toLowerCase();
    if (!term) return roles;
    return roles.filter(role => 
      role.name?.toLowerCase().includes(term) ||
      role.display_name?.toLowerCase().includes(term) ||
      role.description?.toLowerCase().includes(term)
    );
  }, [roles, roleSearchTerm]);

  // Estados para gerenciar empresas
  const [companies, setCompanies] = useState([]);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [companyUserLimit, setCompanyUserLimit] = useState(null);

  // Verificar acesso e perfis usando permissões efetivas com fallback por role
  const isAdmin = !!hasPermission('canManageRoles', ['ADM', 'ADMIN']);
  const isManager = !!hasPermission('canManageGroups', ['GERENCIAL']);
  const hasAccess = !isTrialOrUnitarioProfile && !!(
    hasPermission('canCreateUser', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('canEditUser', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('canManageGroups', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('canManageRoles', ['ADM', 'ADMIN']) ||
    hasPermission('canViewCompanies', ['ADM', 'ADMIN', 'GERENCIAL', 'QA', 'DEV'])
  );
  
  // Logs de debug removidos para produção
  
  // Definir permissões baseadas no perfil (fallback para compatibilidade)
  const fallbackPermissions = {
    canCreateUser: (isAdmin || isManager) && !isTrialOrUnitarioProfile,
    canEditUser: isAdmin || isManager,
    canDeleteUser: isAdmin,
    canCreateApiUser: isAdmin,
    canCreateCompany: isAdmin,
    canManageGroups: isAdmin || isManager,
    canManageRoles: isAdmin,
    canCreateRole: isAdmin,
    canEditRole: isAdmin,
    canDeleteRole: isAdmin,
    // Ajuste: perfil GERENCIAL não deve ver Empresas
    canViewCompanies: hasPermission('canViewCompanies', ['ADM', 'ADMIN', 'QA', 'DEV']),
    canEditCompanies: isAdmin,
    canDeleteCompanies: isAdmin
  };
  

  
  // Usar permissões efetivas quando disponíveis, senão usar as de fallback
  // Garantir que as permissões de função estejam sempre definidas para admins
  // IMPORTANTE: Sempre garantir que admins tenham acesso às empresas, mesmo durante carregamento
  const basePermissions = Object.keys(effectivePermissions).length > 0 ? effectivePermissions : fallbackPermissions;
  
  const permissions = {
    ...basePermissions,
    // Exclusivo ADM: gerenciar área de Funções
    canManageRoles: !!isAdmin,
    // Garantir que admins sempre tenham permissões de função
    canEditRole: basePermissions.canEditRole || isAdmin,
    canDeleteRole: basePermissions.canDeleteRole || isAdmin,
    canCreateRole: basePermissions.canCreateRole || isAdmin,
    // CORREÇÃO: Garantir que a permissão de visualizar empresas seja estável e ocultar para GERENCIAL
    canViewCompanies: !!((basePermissions.canViewCompanies || fallbackPermissions.canViewCompanies) && !isGerencialProfile),
    // Política: Somente administradores podem criar/editar/excluir empresas
    canCreateCompany: !!isAdmin,
    canEditCompanies: !!isAdmin,
    canDeleteCompanies: !!isAdmin
  };
  

  


  // Carregar usuários e grupos
  useEffect(() => {
    if (authLoading) return;
    if (hasAccess) {
      fetchUsers();
      fetchEffectivePermissions();
      // Carregar grupos quando houver permissão de leitura
      if (hasPermission('groups.read', ['ADM', 'ADMIN', 'GERENCIAL'])) {
        fetchGroups();
      }
      // Carregar roles quando houver permissão de leitura
      if (isAdmin) {
        fetchRoles();
      }
      // CORREÇÃO: Usar fallbackPermissions para evitar dependência circular
      if (hasPermission('canViewCompanies', ['ADM', 'ADMIN', 'QA', 'DEV'])) {
        fetchCompanies();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, isAdmin, user?.id, authLoading]);

  // UseEffect separado para carregar empresas quando permissões efetivas mudarem
  useEffect(() => {
    if (
      hasAccess &&
      Object.keys(effectivePermissions).length > 0 &&
      effectivePermissions.canViewCompanies &&
      !isGerencialProfile
    ) {
      fetchCompanies();
    }
  }, [effectivePermissions, hasAccess, isGerencialProfile]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Buscar mais usuários do backend para evitar limitar à primeira página
      const params = new URLSearchParams({ page: 1, limit: 1000, include_inactive: 'true' });
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const response = await fetch(getApiUrl(`users?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : data.data || []);
        const apiLimit = data?.meta?.company_user_limit;
        if (apiLimit === null || typeof apiLimit === 'undefined') {
          setCompanyUserLimit(null);
        } else if (Number.isFinite(Number(apiLimit))) {
          setCompanyUserLimit(Number(apiLimit));
        } else {
          setCompanyUserLimit(null);
        }
      } else {
        console.error('Erro ao carregar usuários:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('groups'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGroups(Array.isArray(data) ? data : []);
      } else {
        console.error('Erro ao carregar grupos:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('roles'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(Array.isArray(data) ? data : []);
      } else {
        console.error('Erro ao carregar roles:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('companies'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(Array.isArray(data.data) ? data.data : []);
      } else {
        console.error('Erro ao carregar empresas:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };



  const fetchEffectivePermissions = async () => {
    if (authLoading || !user || !user.id || isLoadingPermissions) return;
    
    setIsLoadingPermissions(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${user.id}/effective-permissions`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.effective_permissions) {
          setEffectivePermissions(data.data.effective_permissions);
          setUserGroups(data.data.groups || []);
        }
      } else {
        console.error('Erro ao carregar permissões efetivas:', response.statusText);
      }
    } catch (error) {
      console.error('Erro ao carregar permissões efetivas:', error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  // Filtrar usuários baseado na pesquisa (memoizado)
  const filteredUsers = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return users;
    return users.filter(user => 
      user.first_name?.toLowerCase().includes(term) ||
      user.last_name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.company_name?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const companyUsersTotal = Array.isArray(users) ? users.length : 0;
  const enterpriseLimitRaw = process.env.REACT_APP_ENTERPRISE_USER_LIMIT || process.env.REACT_APP_GERENCIAL_USER_LIMIT || '';
  const fallbackLimit = Number.isFinite(Number(enterpriseLimitRaw)) && Number(enterpriseLimitRaw) > 0
    ? Number(enterpriseLimitRaw)
    : null;
  const enterpriseUserLimit = companyUserLimit !== null ? companyUserLimit : fallbackLimit;
  const remainingUserSlots = enterpriseUserLimit !== null
    ? Math.max(enterpriseUserLimit - companyUsersTotal, 0)
    : null;

  // Filtrar empresas baseado na pesquisa (memoizado)
  const filteredCompanies = useMemo(() => {
    const term = (companySearchTerm || '').toLowerCase();
    if (!term) return companies;
    return companies.filter(company => 
      company.name?.toLowerCase().includes(term) ||
      company.description?.toLowerCase().includes(term) ||
      company.contact_email?.toLowerCase().includes(term)
    );
  }, [companies, companySearchTerm]);

  // Handlers
  const handleUserAdded = () => {
    setShowAddUserModal(false);
    fetchUsers();
  };

  const handleApiUserAdded = () => {
    setShowAddApiUserModal(false);
    fetchUsers();
  };

  const handleCompanyAdded = () => {
    setShowCompanyModal(false);
    fetchCompanies(); // Recarregar lista de empresas
  };

  const handleCompanyUpdated = () => {
    setShowEditCompanyModal(false);
    setEditingCompany(null);
    fetchCompanies();
    fetchUsers(); // Recarregar usuários para refletir mudanças
  };

  const handleCompanyDeleted = () => {
    setShowDeleteCompanyModal(false);
    setEditingCompany(null);
    fetchCompanies();
  };

  const handleGroupAdded = () => {
    setShowAddGroupModal(false);
    fetchGroups();
  };

  const handleRoleAdded = async (roleData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('roles'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });

      if (response.ok) {
        await fetchRoles();
        setShowAddRoleModal(false);
        showSuccess('Função criada com sucesso!');
      } else {
        let errMsg = 'Erro ao criar função';
        try {
          const errData = await response.json();
          errMsg = errData?.error || errData?.message || errMsg;
        } catch (_) {}
        throw new Error(errMsg);
      }
    } catch (error) {
      console.error('Erro ao criar função:', error);
      throw error;
    }
  };

  const handleRoleUpdated = async (roleData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`roles/${editingRole.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });

      if (response.ok) {
        await fetchRoles();
        setShowEditRoleModal(false);
        setEditingRole(null);
      } else {
        throw new Error('Erro ao atualizar função');
      }
    } catch (error) {
      console.error('Erro ao atualizar função:', error);
      throw error;
    }
  };

  const handleRoleDeleted = async (roleId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`roles/${roleId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchRoles();
        setShowDeleteRoleModal(false);
        setEditingRole(null);
      } else {
        throw new Error('Erro ao excluir função');
      }
    } catch (error) {
      console.error('Erro ao excluir função:', error);
      throw error;
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowEditGroupModal(true);
  };

  const handleGroupUpdated = () => {
    setShowEditGroupModal(false);
    setEditingGroup(null);
    fetchGroups();
  };

  const handleDeleteGroup = (group) => {
    setEditingGroup(group);
    setShowDeleteGroupModal(true);
  };

  const handleGroupDeleted = () => {
    setShowDeleteGroupModal(false);
    setEditingGroup(null);
    fetchGroups();
  };

  const handleUserUpdated = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditUserModal(true);
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${userId}/toggle-status`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        fetchUsers();
      } else {
        alert('Erro ao alterar status do usuário');
      }
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      alert('Erro ao alterar status do usuário');
    }
  };

  const handleApproveUser = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${targetUser.id}/approve`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.message || 'Erro ao aprovar usuário');
      }

      showSuccess(`Usuário ${targetUser.first_name} aprovado com sucesso.`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao aprovar usuário:', error);
      showError(error?.message || 'Erro ao aprovar usuário.');
    }
  };

  const handleRejectUser = async (targetUser) => {
    const confirmed = window.confirm(`Deseja rejeitar o cadastro de ${targetUser.first_name} ${targetUser.last_name}?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${targetUser.id}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.message || 'Erro ao rejeitar usuário');
      }

      showSuccess(`Cadastro de ${targetUser.first_name} rejeitado.`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao rejeitar usuário:', error);
      showError(error?.message || 'Erro ao rejeitar usuário.');
    }
  };

  // Se não tem acesso, mostrar mensagem
  if (!hasAccess) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>🚫 Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta funcionalidade.</p>
          {isTrialOrUnitarioProfile ? (
            <p>Gerenciamento de usuários não está disponível para contas individuais.</p>
          ) : (
            <p>Fale com quem cuida dos usuários na sua empresa se precisar de acesso.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-layout">
        {/* Main Content */}
        <div className="user-management-main">
          {/* Header */}
          <div className="page-header">
          <div className="header-content">
            <div className="header-left">
              <h1>👥 Usuários e Funções</h1>
              <p>Gerencie usuários, grupos e permissões do sistema</p>
            </div>
            <div className="header-actions">
              {activeTab === 'users' && permissions.canCreateUser && (
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddUserModal(true)}
                >
                  ➕ Usuário
                </button>
              )}
              {activeTab === 'users' && permissions.canCreateApiUser && (
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowAddApiUserModal(true)}
                >
                  🔑 Usuário da API
                </button>
              )}
            </div>
          </div>
        </div>

          {/* Navigation Tabs */}
          <nav className="main-tab-navigation">
            <div className="main-nav-tabs-container">
              <button 
                className={`main-nav-tab ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <span className="tab-icon">👥</span>
                <span className="tab-label">Usuários</span>
              </button>
              <button 
                className={`main-nav-tab ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                <span className="tab-icon">👨‍👩‍👧‍👦</span>
                <span className="tab-label">Grupos</span>
              </button>
              {isAdmin && (
                <button 
                  className={`main-nav-tab ${activeTab === 'roles' ? 'active' : ''}`}
                  onClick={() => setActiveTab('roles')}
                >
                  <span className="tab-icon">🔐</span>
                  <span className="tab-label">Funções</span>
                </button>
              )}
              {permissions.canViewCompanies && !isGerencialProfile && (
                <button 
                  className={`main-nav-tab ${activeTab === 'companies' ? 'active' : ''}`}
                  onClick={() => setActiveTab('companies')}
                >
                  <span className="tab-icon">🏢</span>
                  <span className="tab-label">Empresas</span>
                </button>
              )}
            </div>
          </nav>

          <div className="tab-content">
            {/* Aba de Usuários */}
            {activeTab === 'users' && (
              <div className="users-section">
                <div className="section-header">
                  <h2>Usuários</h2>
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="🔍 Pesquisar usuários..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {isGerencialProfile && (
                  <div className={`user-limit-banner ${remainingUserSlots !== null && remainingUserSlots <= 2 ? 'warning' : ''}`}>
                    <div className="user-limit-item">
                      <span className="label">Usuários cadastrados</span>
                      <strong>{companyUsersTotal}</strong>
                    </div>
                    <div className="user-limit-item">
                      <span className="label">Limite do plano</span>
                      <strong>{enterpriseUserLimit !== null ? enterpriseUserLimit : 'Sem limite definido'}</strong>
                    </div>
                    <div className="user-limit-item">
                      <span className="label">Disponíveis para cadastro</span>
                      <strong>{remainingUserSlots !== null ? remainingUserSlots : 'Ilimitado'}</strong>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando usuários...</p>
                  </div>
                ) : (
                  <div className="um-list-wrapper">
                    {filteredUsers.length === 0 ? (
                      <div className="no-data">
                        <p>Nenhum usuário encontrado.</p>
                      </div>
                    ) : (
                      <div className="um-grid">
                        {filteredUsers.map(user => {
                          const approvalStatus = String(user.approval_status || 'approved').toLowerCase();
                          return (
                            <div key={user.id} className="um-card">
                              <div className="um-card-top">
                                <span className={`role-badge ${getGlobalUserRoleBadgeInfo(user.role).class}`}>
                                  {getGlobalUserRoleBadgeInfo(user.role).text}
                                </span>
                                <div className="status-stack">
                                  <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                                    {user.is_active ? 'Ativo' : 'Inativo'}
                                  </span>
                                  {approvalStatus !== 'approved' && (
                                    <span className={`status-badge status-approval ${approvalStatus}`}>
                                      {approvalStatus === 'pending' ? 'Pendente' : 'Rejeitado'}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="um-card-name">{user.first_name} {user.last_name}</div>
                              <div className="um-card-email">{user.email}</div>

                              <div className="um-card-meta">
                                <span title="Empresa">🏢 {user.company_name || 'Não informado'}</span>
                                <span title="Data de criação">📅 {new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
                              </div>

                              <div className="um-card-actions">
                                {permissions.canEditUser && (
                                  <button
                                    className="btn-action btn-edit"
                                    onClick={() => handleEditUser(user)}
                                    title="Editar usuário"
                                  >
                                    ✏️ Editar
                                  </button>
                                )}
                                {permissions.canEditUser && (
                                  <button
                                    className="btn-action btn-toggle"
                                    onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                                    title={user.is_active ? 'Desativar usuário' : 'Ativar usuário'}
                                  >
                                    {user.is_active ? '🔒 Desativar' : '🔓 Ativar'}
                                  </button>
                                )}
                                {permissions.canEditUser && approvalStatus === 'pending' && (
                                  <>
                                    <button
                                      className="btn-action btn-approve"
                                      onClick={() => handleApproveUser(user)}
                                      title="Aprovar cadastro"
                                    >
                                      ✅ Aprovar
                                    </button>
                                    <button
                                      className="btn-action btn-reject"
                                      onClick={() => handleRejectUser(user)}
                                      title="Rejeitar cadastro"
                                    >
                                      ⛔ Rejeitar
                                    </button>
                                  </>
                                )}
                                {permissions.canDeleteUser && (
                                  <button
                                    className="btn-action btn-delete"
                                    onClick={() => { setUserToDelete(user); setShowDeleteUserModal(true); }}
                                    title="Excluir usuário"
                                  >
                                    🗑️ Excluir
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Aba de Grupos */}
            {activeTab === 'groups' && (
              <div className="groups-section">
                <div className="section-header">
                  <h2>Grupos de Usuários</h2>
                  {hasPermission('groups.create', ['ADM', 'ADMIN', 'GERENCIAL']) && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowAddGroupModal(true)}
                    >
                      ➕ Novo Grupo
                    </button>
                  )}
                </div>
                
                {loading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando grupos...</p>
                  </div>
                ) : groups.length === 0 ? (
                  <div className="no-data">
                    <p>Nenhum grupo encontrado. Crie o primeiro grupo clicando em "Novo Grupo".</p>
                  </div>
                ) : (
                  <div className="um-grid um-grid-compact">
                    {groups.map(group => (
                      <div key={group.id} className="um-card">
                        <div className="um-card-name">👥 {group.name}</div>
                        <div className="um-card-meta">
                          <span title="Membros">{group.member_count || 0} usuário{group.member_count === 1 ? '' : 's'}</span>
                          <span title="Data de criação">📅 {new Date(group.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="um-card-actions">
                          {permissions.canEditUser && (
                            <button
                              className="btn-action btn-edit"
                              onClick={() => handleEditGroup(group)}
                              title="Editar grupo"
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {permissions.canDeleteUser && (
                            <button
                              className="btn-action btn-delete"
                              onClick={() => handleDeleteGroup(group)}
                              title="Excluir grupo"
                            >
                              🗑️ Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Aba de Funções */}
            {activeTab === 'roles' && isAdmin && (
              <div className="roles-section">
                <div className="section-header">
                  <h2>Funções e Permissões</h2>
                  {isAdmin && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowAddRoleModal(true)}
                    >
                      Nova Função
                    </button>
                  )}
                </div>

                {/* Barra de pesquisa */}
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Pesquisar funções..."
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>

                {/* Funções */}
                {loading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando funções...</p>
                  </div>
                ) : filteredRoles.length === 0 ? (
                  <div className="no-data">
                    <p>{roleSearchTerm ? 'Nenhuma função encontrada com os critérios de pesquisa.' : 'Nenhuma função cadastrada.'}</p>
                  </div>
                ) : (
                  <div className="um-grid">
                    {filteredRoles.map(role => (
                      <div key={role.id} className="um-card">
                        <div className="um-card-name">🔐 {role.display_name}</div>
                        <div className="um-card-role-code">{role.name}</div>
                        <div className="um-card-description">{role.description || 'Sem descrição'}</div>
                        <div className="um-card-meta">
                          <span title="Permissões">
                            {role.permissions && role.permissions.length > 0
                              ? `${role.permissions.length} permissão${role.permissions.length !== 1 ? 'ões' : ''}`
                              : 'Nenhuma permissão'}
                          </span>
                          <span title="Criado em">📅 {role.created_at ? new Date(role.created_at).toLocaleDateString('pt-BR') : 'N/A'}</span>
                        </div>
                        <div className="um-card-actions">
                          {permissions.canEditRole && (
                            <button
                              className="btn-action btn-edit"
                              onClick={() => {
                                setEditingRole(role);
                                setShowEditRoleModal(true);
                              }}
                              title="Editar função"
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {permissions.canDeleteRole && (
                            <button
                              className="btn-action btn-delete"
                              onClick={() => {
                                setEditingRole(role);
                                setShowDeleteRoleModal(true);
                              }}
                              title="Excluir função"
                            >
                              🗑️ Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Estatísticas */}
                <div className="stats-container">
                  <div className="stat-card total-roles">
                    <h3>{roles.length}</h3>
                    <p>Total de Funções</p>
                  </div>
                  <div className="stat-card roles-with-permissions">
                    <h3>{roles.filter(role => role.permissions && role.permissions.length > 0).length}</h3>
                    <p>Funções com Permissões</p>
                  </div>
                </div>
              </div>
            )}

            {/* Aba de Empresas */}
            {activeTab === 'companies' && permissions.canViewCompanies && !isGerencialProfile && (
              <div className="companies-section">
                <div className="section-header">
                  <h2>Gerenciamento de Empresas</h2>
                  {permissions.canCreateCompany && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowCompanyModal(true)}
                    >
                      ➕ Nova Empresa
                    </button>
                  )}
                </div>

                {/* Barra de pesquisa */}
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Pesquisar empresas..."
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>

                {/* Empresas */}
                {loading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Carregando empresas...</p>
                  </div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="no-data">
                    <p>{companySearchTerm ? 'Nenhuma empresa encontrada com os critérios de pesquisa.' : 'Nenhuma empresa cadastrada.'}</p>
                  </div>
                ) : (
                  <div className="um-grid">
                    {filteredCompanies.map(company => (
                      <div key={company.id} className="um-card">
                        <div className="um-card-top">
                          <span className={`status-badge ${company.status === 'active' ? 'active' : 'inactive'}`}>
                            {company.status === 'active' ? 'ATIVA' : 'INATIVA'}
                          </span>
                        </div>
                        <div className="um-card-name">🏢 {company.name}</div>
                        <div className="um-card-description">{company.description || 'Sem descrição'}</div>
                        <div className="um-card-meta">
                          <span title="E-mail de contato">✉️ {company.contact_email || 'Não informado'}</span>
                          <span title="Telefone">📞 {company.contact_phone || 'Não informado'}</span>
                          <span title="Usuários">👥 {company.user_count || 0} usuário{company.user_count === 1 ? '' : 's'}</span>
                          <span title="Criado em">📅 {new Date(company.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="um-card-actions">
                          {permissions.canEditCompanies && (
                            <button
                              className="btn-action btn-edit"
                              onClick={() => {
                                setEditingCompany(company);
                                setShowEditCompanyModal(true);
                              }}
                              title="Editar empresa"
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {permissions.canDeleteCompanies && (
                            <button
                              className="btn-action btn-delete"
                              onClick={() => {
                                setEditingCompany(company);
                                setShowDeleteCompanyModal(true);
                              }}
                              title="Excluir empresa"
                            >
                              🗑️ Excluir
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Estatísticas */}
                <div className="stats-container">
                  <div className="stat-card total-companies">
                    <h3>{companies.length}</h3>
                    <p>Total de Empresas</p>
                  </div>
                  <div className="stat-card active-companies">
                    <h3>{companies.filter(company => company.status === 'active').length}</h3>
                    <p>Empresas Ativas</p>
                  </div>
                  <div className="stat-card total-company-users">
                    <h3>{companies.reduce((total, company) => total + Number(company.user_count || 0), 0)}</h3>
                    <p>Total de Usuários</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Right Sidebar */}
        <div className="user-management-right-panel">
          <div className="panel-section stats-section">
            <div className="section-header">
              <h3>📊 Estatísticas do Sistema</h3>
              <span className="update-time">Atualizado agora</span>
            </div>
            <div className="modern-stats-grid">
              <div className="modern-stat-card total-users">
                <div className="stat-icon-wrapper">
                  <span className="stat-icon">👥</span>
                </div>
                <div className="stat-content">
                  <span className="stat-number">{users.length}</span>
                  <span className="stat-label">Total de Usuários</span>
                  <div className="stat-trend">
                    <span className="trend-indicator positive">↗️ +2 este mês</span>
                  </div>
                </div>
              </div>
              
              <div className="modern-stat-card active-users">
                <div className="stat-icon-wrapper">
                  <span className="stat-icon">✅</span>
                </div>
                <div className="stat-content">
                  <span className="stat-number">{users.filter(u => u.is_active).length}</span>
                  <span className="stat-label">Usuários Ativos</span>
                  <div className="stat-trend">
                    <span className="trend-indicator positive">
                      {Math.round((users.filter(u => u.is_active).length / users.length) * 100)}% ativo
                    </span>
                  </div>
                </div>
              </div>
              
              {!isGerencialProfile && (
              <div className="modern-stat-card admin-users">
                <div className="stat-icon-wrapper">
                  <span className="stat-icon">👑</span>
                </div>
                <div className="stat-content">
                  <span className="stat-number">{users.filter(u => u.role === 'ADM').length}</span>
                  <span className="stat-label">Administradores</span>
                  <div className="stat-trend">
                    <span className="trend-indicator neutral">
                      {Math.round((users.filter(u => u.role === 'ADM').length / users.length) * 100)}% do total
                    </span>
                  </div>
                </div>
              </div>
              )}
              
              {!isGerencialProfile && (
              <div className="modern-stat-card companies">
                <div className="stat-icon-wrapper">
                  <span className="stat-icon">🏢</span>
                </div>
                <div className="stat-content">
                  <span className="stat-number">{companies.filter(c => c.status === 'active').length}</span>
                  <span className="stat-label">Empresas Ativas</span>
                  <div className="stat-trend">
                    <span className="trend-indicator positive">Múltiplas organizações</span>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>

          <div className="panel-section">
            <h3>🔐 Permissões</h3>
            <div className="permissions-info">
              <div className="permission-item">
                <span className="permission-icon">✅</span>
                <span>Criar Usuários: {permissions.canCreateUser ? 'Sim' : 'Não'}</span>
              </div>
              <div className="permission-item">
                <span className="permission-icon">✅</span>
                <span>Editar Usuários: {permissions.canEditUser ? 'Sim' : 'Não'}</span>
              </div>
              <div className="permission-item">
                <span className="permission-icon">✅</span>
                <span>Excluir Usuários: {permissions.canDeleteUser ? 'Sim' : 'Não'}</span>
              </div>
              <div className="permission-item">
                <span className="permission-icon">✅</span>
                <span>API Tokens: {permissions.canCreateApiUser ? 'Sim' : 'Não'}</span>
              </div>
              <div className="permission-item">
                <span className="permission-icon">✅</span>
                <span>Cadastro Empresa: {permissions.canCreateCompany ? 'Sim' : 'Não'}</span>
              </div>
              {permissions.canManageGroups && (
                <div className="permission-item">
                  <span className="permission-icon">✅</span>
                  <span>Gerenciar Grupos: Sim</span>
                </div>
              )}
            </div>
          </div>

          {userGroups.length > 0 && (
            <div className="panel-section">
              <h3>👥 Meus Grupos</h3>
              <div className="user-groups-info">
                {userGroups.map(group => (
                  <div key={group.id} className="group-item">
                    <div className="group-header">
                      <span className="group-icon">🏷️</span>
                      <span className="group-name">{group.name}</span>
                    </div>
                    {group.description && (
                      <div className="group-description">{group.description}</div>
                    )}
                  </div>
                ))}
                <div className="groups-note">
                  <small>💡 Suas permissões incluem as herdadas destes grupos</small>
                </div>
              </div>
            </div>
          )}

          <div className="panel-section">
            <h3>ℹ️ Informações</h3>
            <div className="info-list">
              <div className="info-item">
                <strong>Perfil Atual:</strong>
                <span className="user-role">{user?.role || 'N/A'}</span>
              </div>
              <div className="info-item">
                <strong>Última Atualização:</strong>
                <span>{new Date().toLocaleDateString('pt-BR')}</span>
              </div>
              <div className="info-item">
                <strong>Sistema:</strong>
                <span>Gestão de QA</span>
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Modals */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onUserAdded={handleUserAdded}
          isAdmin={isAdmin}
        />
      )}

      {showAddApiUserModal && (
        <AddApiUserModal
          onClose={() => setShowAddApiUserModal(false)}
          onApiUserAdded={handleApiUserAdded}
          isAdmin={isAdmin}
        />
      )}

      {showCompanyModal && (
        <CompanyModal
          onClose={() => setShowCompanyModal(false)}
          onCompanyAdded={handleCompanyAdded}
          isAdmin={isAdmin}
        />
      )}

      {showEditCompanyModal && editingCompany && (
        <EditCompanyModal
          company={editingCompany}
          onClose={() => setShowEditCompanyModal(false)}
          onCompanyUpdated={handleCompanyUpdated}
          isAdmin={isAdmin}
        />
      )}

      {showDeleteCompanyModal && editingCompany && (
        <DeleteCompanyModal
          company={editingCompany}
          onClose={() => setShowDeleteCompanyModal(false)}
          onCompanyDeleted={handleCompanyDeleted}
        />
      )}

      {showEditUserModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setShowEditUserModal(false)}
          onUserUpdated={handleUserUpdated}
          isAdmin={isAdmin}
        />
      )}

      {showAddGroupModal && (
        <AddGroupModal
          onClose={() => setShowAddGroupModal(false)}
          onGroupAdded={handleGroupAdded}
          users={users}
          isAdmin={isAdmin}
        />
      )}

      {showEditGroupModal && editingGroup && (
        <EditGroupModal
          group={editingGroup}
          onClose={() => setShowEditGroupModal(false)}
          onGroupUpdated={handleGroupUpdated}
          users={users}
          isAdmin={isAdmin}
        />
      )}

      {showDeleteGroupModal && editingGroup && (
        <DeleteGroupModal
          group={editingGroup}
          onClose={() => setShowDeleteGroupModal(false)}
          onGroupDeleted={handleGroupDeleted}
        />
      )}

      {showDeleteUserModal && userToDelete && (
        <DeleteUserModal
          user={userToDelete}
          onClose={() => { setShowDeleteUserModal(false); setUserToDelete(null); }}
          onUserDeleted={() => { setShowDeleteUserModal(false); setUserToDelete(null); fetchUsers(); }}
        />
      )}

      {showAddRoleModal && (
        <AddRoleModal
          onClose={() => setShowAddRoleModal(false)}
          onRoleAdded={handleRoleAdded}
        />
      )}

      {showEditRoleModal && editingRole && (
        <EditRoleModal
          role={editingRole}
          onClose={() => setShowEditRoleModal(false)}
          onRoleUpdated={handleRoleUpdated}
        />
      )}

      {showDeleteRoleModal && editingRole && (
        <DeleteRoleModal
          role={editingRole}
          onClose={() => setShowDeleteRoleModal(false)}
          onRoleDeleted={handleRoleDeleted}
        />
      )}
    </div>
  );
};

// Modal para Adicionar Usuário
const AddUserModal = ({ onClose, onUserAdded, isAdmin }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'QA',
    company_name: user?.company_name || '',
    company_id: null,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const roles = [
    { value: 'QA', label: 'QA', description: 'Analista de Qualidade' },
    { value: 'DEV', label: 'DEV', description: 'Desenvolvedor' },
    { value: 'GERENCIAL', label: 'GERENCIAL', description: 'Gestor de equipe' }
  ];

  if (isAdmin) {
    roles.push(
      { value: 'ADM', label: 'ADM', description: 'Administrador do sistema' }
    );
  }

  // Validação de senha consistente com Register.js
  const validatePassword = (password) => {
    if (!password) {
      return 'Senha é obrigatória';
    }
    if (password.length < 8 || password.length > 20) {
      return 'Senha deve ter entre 8 e 20 caracteres';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Senha deve conter pelo menos uma letra minúscula';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Senha deve conter pelo menos uma letra maiúscula';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Senha deve conter pelo menos um número';
    }
    if (!(/(?=.*[@$!%*?&])/.test(password))) {
      return 'Senha deve conter pelo menos um caractere especial (@$!%*?&)';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(getApiUrl('companies'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data.data) ? data.data : [];
          setCompanies(list);
          // Default para a empresa do usuário logado, se existir na lista
          const currentCompany = list.find(c => c.id === user?.company_id) || list.find(c => c.name === user?.company_name);
          if (currentCompany) {
            setFormData(prev => ({ ...prev, company_id: currentCompany.id, company_name: currentCompany.name }));
          }
        }
      } catch (err) {
        console.error('Erro ao carregar empresas:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };

    if (isAdmin) fetchCompanies();
  }, [isAdmin, user?.company_id, user?.company_name]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Nome é obrigatório';
    } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(formData.first_name.trim())) {
      newErrors.first_name = 'Use apenas letras e espaços';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Sobrenome é obrigatório';
    } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(formData.last_name.trim())) {
      newErrors.last_name = 'Use apenas letras e espaços';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    if (isAdmin && !formData.company_id) {
      newErrors.company_id = 'Selecione uma empresa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { confirmPassword, ...userData } = formData;
      const roleMap = { QA: 'QA', DEV: 'DEV', GERENCIAL: 'GERENCIAL', ADM: 'ADM' };
      const apiData = { ...userData, role: roleMap[userData.role] };
      if (isAdmin && formData.company_id) {
        apiData.company_id = formData.company_id;
      }
      
      const response = await fetch(getApiUrl('users'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        alert('Usuário criado com sucesso!');
        setSubmitError('');
        onUserAdded();
      } else {
        const result = await response.json();
        const msg = result?.message || 'Erro ao criar usuário';
        alert(msg);
        setSubmitError(msg);
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      const msg = 'Erro ao criar usuário. Tente novamente.';
      alert(msg);
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>➕ Adicionar Novo Usuário</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          {submitError && (
            <div className="error-message" style={{ marginBottom: '12px' }}>
              {submitError}
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">Nome *</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                  if (allowed.includes(e.key)) return;
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + 1;
                  if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                  if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                  e.preventDefault();
                }}
                onBeforeInput={(e) => {
                  if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                  if (/^[\p{L}\s]+$/u.test(e.data)) {
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + e.data.length;
                    if (willLen > 100) e.preventDefault();
                    return;
                  }
                  e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + text.length;
                  if (willLen > 100) e.preventDefault();
                }}
                onDrop={(e) => e.preventDefault()}
                pattern="^[A-Za-zÀ-ÿ\\s]+$"
                title="Use apenas letras (A–Z, á–ú) e espaços"
                className={errors.first_name ? 'error' : ''}
                placeholder="Digite o nome"
                maxLength={100}
              />
              {errors.first_name && <span className="error-message">{errors.first_name}</span>}
              <div className={formData.first_name.length >= 100 ? 'char-counter limit' : 'char-counter'}>
                {formData.first_name.length}/100
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Sobrenome *</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                  if (allowed.includes(e.key)) return;
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + 1;
                  if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                  if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                  e.preventDefault();
                }}
                onBeforeInput={(e) => {
                  if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                  if (/^[\p{L}\s]+$/u.test(e.data)) {
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + e.data.length;
                    if (willLen > 100) e.preventDefault();
                    return;
                  }
                  e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + text.length;
                  if (willLen > 100) e.preventDefault();
                }}
                onDrop={(e) => e.preventDefault()}
                pattern="^[A-Za-zÀ-ÿ\\s]+$"
                title="Use apenas letras (A–Z, á–ú) e espaços"
                className={errors.last_name ? 'error' : ''}
                placeholder="Digite o sobrenome"
                maxLength={100}
              />
              {errors.last_name && <span className="error-message">{errors.last_name}</span>}
              <div className={formData.last_name.length >= 100 ? 'char-counter limit' : 'char-counter'}>
                {formData.last_name.length}/100
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="Digite o email"
                maxLength={100}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
              <div className={formData.email.length >= 100 ? 'char-counter limit' : 'char-counter'}>
                {formData.email.length}/100
              </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Senha *</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'error' : ''}
                  placeholder="Digite a senha"
                  maxLength={20}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
              <div className={formData.password.length >= 20 ? 'char-counter limit' : 'char-counter'}>
                {formData.password.length}/20
              </div>
              <div className="password-requirements">
                <small>
                  A senha deve conter: 8-20 caracteres, pelo menos 1 letra maiúscula,
                  1 letra minúscula, 1 número e 1 caractere especial (@$!%*?&).
                </small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Senha *</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Confirme a senha"
                  maxLength={20}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              <div className={(formData.confirmPassword?.length ?? 0) >= 20 ? 'char-counter limit' : 'char-counter'}>
                {(formData.confirmPassword?.length ?? 0)}/20
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Função *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label} - {role.description}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="company_name">Nome da Empresa *</label>
            {isAdmin ? (
              <select
                id="company_id"
                name="company_id"
                value={formData.company_id ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const company = companies.find(c => c.id === id);
                  setFormData(prev => ({ ...prev, company_id: id || null, company_name: company?.name || '' }));
                  if (errors.company_id) setErrors(prev => ({ ...prev, company_id: '' }));
                }}
                disabled={loadingCompanies}
                className={errors.company_id ? 'error' : ''}
              >
                <option value="">Selecione uma empresa</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                disabled={!isAdmin}
                className={errors.company_name ? 'error' : ''}
                placeholder="Digite o nome da empresa"
              />
            )}
            {errors.company_id && <span className="error-message">{errors.company_id}</span>}
            {errors.company_name && <span className="error-message">{errors.company_name}</span>}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span className="checkmark"></span>
              Usuário ativo (pode acessar o sistema)
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-danger">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Criando...' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Editar Usuário
const EditUserModal = ({ user, onClose, onUserUpdated, isAdmin }) => {
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    role: user.role || 'QA',
    company_name: user.company_name || '',
    is_active: user.is_active !== undefined ? user.is_active : true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const roles = [
    { value: 'QA', label: 'QA', description: 'Analista de Qualidade' },
    { value: 'DEV', label: 'DEV', description: 'Desenvolvedor' },
    { value: 'GERENCIAL', label: 'GERENCIAL', description: 'Gestor de equipe' }
  ];

  if (isAdmin) {
    roles.push(
      { value: 'ADM', label: 'ADM', description: 'Administrador do sistema' }
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Nome é obrigatório';
    } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(formData.first_name.trim())) {
      newErrors.first_name = 'Use apenas letras e espaços';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Sobrenome é obrigatório';
    } else if (!/^[A-Za-zÀ-ÿ\s]+$/.test(formData.last_name.trim())) {
      newErrors.last_name = 'Use apenas letras e espaços';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Nome da empresa é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const roleMap = { QA: 'QA', DEV: 'DEV', GERENCIAL: 'GERENCIAL', ADM: 'ADM' };
      const apiData = { ...formData, role: roleMap[formData.role] || String(formData.role || '').toUpperCase() };
      
      const response = await fetch(getApiUrl(`users/${user.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        alert('Usuário atualizado com sucesso!');
        onUserUpdated();
      } else {
        const result = await response.json();
        alert(result.message || 'Erro ao atualizar usuário');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('Erro ao atualizar usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>✏️ Editar Usuário</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="edit_first_name">Nome *</label>
          <input
            type="text"
            id="edit_first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
              if (allowed.includes(e.key)) return;
              const el = e.target;
              const cur = el.value || '';
              const selStart = el.selectionStart ?? cur.length;
              const selEnd = el.selectionEnd ?? cur.length;
              const willLen = cur.length - (selEnd - selStart) + 1;
              if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
              if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
              e.preventDefault();
            }}
            onBeforeInput={(e) => {
              if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
              if (/^[\p{L}\s]+$/u.test(e.data)) {
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + e.data.length;
                if (willLen > 100) e.preventDefault();
                return;
              }
              e.preventDefault();
            }}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text');
              if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
              const el = e.target;
              const cur = el.value || '';
              const selStart = el.selectionStart ?? cur.length;
              const selEnd = el.selectionEnd ?? cur.length;
              const willLen = cur.length - (selEnd - selStart) + text.length;
              if (willLen > 100) e.preventDefault();
            }}
            onDrop={(e) => e.preventDefault()}
            pattern="^[A-Za-zÀ-ÿ\\s]+$"
            title="Use apenas letras (A–Z, á–ú) e espaços"
            className={errors.first_name ? 'error' : ''}
            placeholder="Digite o nome"
          />
              {errors.first_name && <span className="error-message">{errors.first_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="edit_last_name">Sobrenome *</label>
          <input
            type="text"
            id="edit_last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
              if (allowed.includes(e.key)) return;
              const el = e.target;
              const cur = el.value || '';
              const selStart = el.selectionStart ?? cur.length;
              const selEnd = el.selectionEnd ?? cur.length;
              const willLen = cur.length - (selEnd - selStart) + 1;
              if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
              if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
              e.preventDefault();
            }}
            onBeforeInput={(e) => {
              if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
              if (/^[\p{L}\s]+$/u.test(e.data)) {
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + e.data.length;
                if (willLen > 100) e.preventDefault();
                return;
              }
              e.preventDefault();
            }}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text');
              if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
              const el = e.target;
              const cur = el.value || '';
              const selStart = el.selectionStart ?? cur.length;
              const selEnd = el.selectionEnd ?? cur.length;
              const willLen = cur.length - (selEnd - selStart) + text.length;
              if (willLen > 100) e.preventDefault();
            }}
            onDrop={(e) => e.preventDefault()}
            pattern="^[A-Za-zÀ-ÿ\\s]+$"
            title="Use apenas letras (A–Z, á–ú) e espaços"
            className={errors.last_name ? 'error' : ''}
            placeholder="Digite o sobrenome"
          />
              {errors.last_name && <span className="error-message">{errors.last_name}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="edit_email">Email *</label>
            <input
              type="email"
              id="edit_email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Digite o email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="edit_role">Função *</label>
            <select
              id="edit_role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label} - {role.description}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit_company_name">Nome da Empresa *</label>
            <input
              type="text"
              id="edit_company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              className={errors.company_name ? 'error' : ''}
              placeholder="Digite o nome da empresa"
            />
            {errors.company_name && <span className="error-message">{errors.company_name}</span>}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span className="checkmark"></span>
              Usuário ativo (pode acessar o sistema)
            </label>
          </div>

          <div className="user-info-note">
            <p><strong>📧 Email atual:</strong> {user.email}</p>
            <p><strong>📅 Criado em:</strong> {new Date(user.created_at).toLocaleDateString('pt-BR')}</p>
            {user.last_login && (
              <p><strong>🕐 Último acesso:</strong> {new Date(user.last_login).toLocaleDateString('pt-BR')}</p>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-cancel">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Adicionar Usuário da API
const AddApiUserModal = ({ onClose, onApiUserAdded, isAdmin }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    expires_at: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const availablePermissions = [
    { value: 'read', label: 'Leitura', description: 'Acesso de leitura aos dados' },
    { value: 'write', label: 'Escrita', description: 'Criação e edição de dados' },
    { value: 'delete', label: 'Exclusão', description: 'Exclusão de dados' },
    { value: 'admin', label: 'Administração', description: 'Acesso administrativo completo' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'Selecione pelo menos uma permissão';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('api-users'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Usuário da API criado com sucesso!\nToken: ${result.token || 'Verifique no sistema'}`);
        onApiUserAdded();
      } else {
        const errorData = await response.json();
        alert(`Erro ao criar usuário da API: ${errorData.message || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao criar usuário da API:', error);
      alert('Erro ao criar usuário da API. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>🔑 Adicionar Usuário da API</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Nome do Token *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Ex: Token para integração X"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Descrição *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={errors.description ? 'error' : ''}
              placeholder="Descreva o propósito deste token de API"
              rows="3"
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-group">
            <label>Permissões *</label>
            <div className="permissions-grid">
              {availablePermissions.map(permission => (
                <div key={permission.value} className="permission-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission.value)}
                      onChange={() => handlePermissionChange(permission.value)}
                    />
                    <span className="permission-name">{permission.label}</span>
                    <small className="permission-description">{permission.description}</small>
                  </label>
                </div>
              ))}
            </div>
            {errors.permissions && <span className="error-message">{errors.permissions}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="expires_at">Data de Expiração (Opcional)</label>
            <input
              type="date"
              id="expires_at"
              name="expires_at"
              value={formData.expires_at}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
            />
            <small className="form-help">Deixe em branco para token sem expiração</small>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para cadastro de empresa
const CompanyModal = ({ onClose, onCompanyAdded, isAdmin }) => {
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    email: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validação básica
      if (!formData.name || !formData.email) {
        throw new Error('Nome e email são obrigatórios');
      }

      // Montar payload conforme API do backend
      const payload = {
        name: formData.name.trim(),
        description: null, // Campo opcional; manter nulo se não houver no formulário
        contact_email: formData.email.trim(),
        contact_phone: (formData.phone || '').trim() || null,
        address: (formData.address || '').trim() || null
      };

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('companies'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Empresa criada com sucesso
        onCompanyAdded();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Erro ao cadastrar empresa');
      }
    } catch (err) {
      setError(err.message || 'Erro ao cadastrar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>🏢 Cadastrar Nova Empresa</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">Nome da Empresa *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Digite o nome da empresa"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="cnpj">CNPJ (Opcional)</label>
            <input
              type="text"
              id="cnpj"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleInputChange}
              placeholder="00.000.000/0000-00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="contato@empresa.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Telefone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(11) 99999-9999"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Endereço</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Endereço completo da empresa"
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Cadastrando...' : 'Cadastrar Empresa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Editar Empresa
const EditCompanyModal = ({ company, onClose, onCompanyUpdated, isAdmin }) => {
  const [formData, setFormData] = useState({
    name: company?.name || '',
    description: company?.description || '',
    // Mapear status inicial do backend ('blocked') para UI ('inactive')
    status: (company?.status === 'blocked') ? 'inactive' : (company?.status || 'active'),
    contact_email: company?.contact_email || '',
    contact_phone: company?.contact_phone || '',
    address: company?.address || ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome da empresa é obrigatório';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Converter status da UI para o esperado pelo backend
      const payload = {
        ...formData,
        status: formData.status === 'inactive' ? 'blocked' : 'active'
      };
      const response = await fetch(getApiUrl(`companies/${company.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onCompanyUpdated();
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.message || 'Erro ao atualizar empresa' });
      }
    } catch (error) {
      setErrors({ submit: 'Erro de conexão. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Editar Empresa</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="form-group">
            <label htmlFor="name">Nome da Empresa *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Nome da empresa"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Descrição</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Descrição da empresa"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="active">Ativa</option>
              <option value="inactive">Inativa</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contact_email">Email de Contato</label>
            <input
              type="email"
              id="contact_email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              placeholder="contato@empresa.com"
              className={errors.contact_email ? 'error' : ''}
            />
            {errors.contact_email && <span className="field-error">{errors.contact_email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone">Telefone de Contato</label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              placeholder="(11) 99999-9999"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Endereço</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Endereço completo da empresa"
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-danger" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Atualizando...' : 'Atualizar Empresa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Excluir Empresa
const DeleteCompanyModal = ({ company, onClose, onCompanyDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      // Usa o helper para construir corretamente a URL da API
      const response = await fetch(getApiUrl(`companies/${company.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        onCompanyDeleted();
      } else {
        let errorMessage = 'Erro ao excluir empresa';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (_) {
          // Caso não haja corpo JSON, mantém mensagem padrão
        }
        setError(errorMessage);
      }
    } catch (error) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🗑️ Excluir Empresa</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}
          
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <p>
              Tem certeza que deseja excluir a empresa <strong>"{company.name}"</strong>?
            </p>
            <div className="warning-details">
              <p>⚠️ <strong>Atenção:</strong> Esta ação não pode ser desfeita.</p>
              <p>📋 Todos os usuários vinculados a esta empresa serão desvinculados.</p>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Excluindo...' : 'Excluir Empresa'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para Excluir Usuário (hard delete)
const DeleteUserModal = ({ user, onClose, onUserDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${user.id}/hard`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        onUserDeleted();
      } else {
        let errorMessage = 'Erro ao excluir usuário definitivamente';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (_) {
          // Ignorar erros de parsing
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao excluir usuário definitivamente:', error);
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🗑️ Excluir Usuário</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <p>
              Tem certeza que deseja excluir o usuário <strong>{user.first_name} {user.last_name}</strong>?
            </p>
            <div className="warning-details">
              <p>⚠️ <strong>Atenção:</strong> Esta ação não pode ser desfeita.</p>
              <p>📋 O usuário será removido definitivamente do sistema.</p>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-danger"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Excluindo...' : 'Excluir Usuário'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para Adicionar Grupo
const AddGroupModal = ({ onClose, onGroupAdded, users, isAdmin }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedUsers: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar usuários baseado na pesquisa
  const filteredUsers = users.filter(user => 
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'O nome do grupo é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('groups'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim(),
          userIds: formData.selectedUsers
        })
      });

      if (response.ok) {
        onGroupAdded();
      } else {
        const error = await response.json();
        setErrors({ submit: error.message || 'Erro ao criar grupo' });
      }
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
      setErrors({ submit: 'Erro ao criar grupo' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>➕ Adicionar Novo Grupo</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {errors.submit && <div className="error-message">{errors.submit}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="group_name">Nome do Grupo *</label>
            <input
              type="text"
              id="group_name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Digite o nome do grupo"
              maxLength={100}
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
            <div className={formData.name.length >= 100 ? 'char-counter limit' : 'char-counter'}>
              {formData.name.length}/100
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="group_description">Descrição</label>
            <textarea
              id="group_description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Digite uma descrição para o grupo (opcional)"
              maxLength={300}
              rows="3"
            />
            <div className={formData.description.length >= 300 ? 'char-counter limit' : 'char-counter'}>
              {formData.description.length}/300
            </div>
          </div>

          <div className="form-group">
            <label>Selecionar Usuários</label>
            <div className="user-search">
              <input
                type="text"
                placeholder="🔍 Pesquisar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <div className="no-users">
                  <p>Nenhum usuário encontrado.</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div key={user.id} className="user-item">
                    <label className="user-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.selectedUsers.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                      />
                      <div className="user-info">
                        <div className="user-name">
                          <strong>{user.first_name} {user.last_name}</strong>
                        </div>
                        <div className="user-details">
                          <span className="user-email">{user.email}</span>
                          <span className={`user-role role-badge badge-${user.role}`}>
                            {user.role}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            {formData.selectedUsers.length > 0 && (
              <div className="selected-count">
                {formData.selectedUsers.length} usuário(s) selecionado(s)
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Grupo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Editar Grupo
const EditGroupModal = ({ group, onClose, onGroupUpdated, users, isAdmin }) => {
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    selectedUsers: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Carregar membros do grupo
  useEffect(() => {
    if (group?.id) {
      fetchGroupMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id]);

  const fetchGroupMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`groups/${group.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const groupData = await response.json();
        const memberIds = groupData.members?.map(member => member.id) || [];
        setFormData(prev => ({
          ...prev,
          selectedUsers: memberIds
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar membros do grupo:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  const filteredUsers = users.filter(user =>
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome do grupo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`groups/${group.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim(),
          userIds: formData.selectedUsers
        })
      });

      if (response.ok) {
        onGroupUpdated();
      } else {
        const error = await response.json();
        setErrors({ submit: error.message || 'Erro ao atualizar grupo' });
      }
    } catch (error) {
      console.error('Erro ao atualizar grupo:', error);
      setErrors({ submit: 'Erro ao atualizar grupo' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>✏️ Editar Grupo</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {errors.submit && <div className="error-message">{errors.submit}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="edit_group_name">Nome do Grupo *</label>
            <input
              type="text"
              id="edit_group_name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Digite o nome do grupo"
              maxLength={100}
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
            <div className={formData.name.length >= 100 ? 'char-counter limit' : 'char-counter'}>
              {formData.name.length}/100
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="edit_group_description">Descrição</label>
            <textarea
              id="edit_group_description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Descrição opcional do grupo"
              maxLength={300}
              rows="3"
            />
            <div className={formData.description.length >= 300 ? 'char-counter limit' : 'char-counter'}>
              {formData.description.length}/300
            </div>
          </div>

          <div className="form-group">
            <label>Selecionar Usuários</label>
            <div className="user-search">
              <input
                type="text"
                placeholder="🔍 Pesquisar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <div className="no-users">
                  <p>Nenhum usuário encontrado.</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div key={user.id} className="user-item">
                    <label className="user-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.selectedUsers.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                      />
                      <div className="user-info">
                        <div className="user-name">
                          <strong>{user.first_name} {user.last_name}</strong>
                        </div>
                        <div className="user-details">
                          <span className="user-email">{user.email}</span>
                          <span className={`user-role role-badge badge-${user.role}`}>
                            {user.role}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            
            {formData.selectedUsers.length > 0 && (
              <div className="selected-count">
                {formData.selectedUsers.length} usuário(s) selecionado(s)
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-danger" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Atualizando...' : 'Atualizar Grupo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Excluir Grupo
const DeleteGroupModal = ({ group, onClose, onGroupDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`groups/${group.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onGroupDeleted();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao excluir grupo');
      }
    } catch (error) {
      console.error('Erro ao excluir grupo:', error);
      setError('Erro ao excluir grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>🗑️ Excluir Grupo</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          <p>Tem certeza que deseja excluir o grupo <strong>"{group.name}"</strong>?</p>
          <p className="warning-text">
            ⚠️ Esta ação não pode ser desfeita. Todos os usuários serão removidos do grupo.
          </p>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Excluindo...' : 'Excluir Grupo'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para Adicionar Role
const AddRoleModal = ({ onClose, onRoleAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Carregar permissões via API (IDs)
  const [loadingData, setLoadingData] = useState(true);
  const [permissionsByModule, setPermissionsByModule] = useState({});

  useEffect(() => {
    let isMounted = true;
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(getApiUrl('roles/permissions/all'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = resp.ok ? await resp.json() : {};
        if (!isMounted) return;
        setPermissionsByModule(data || {});
      } catch (err) {
        console.error('Erro ao carregar permissões:', err);
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };
    fetchPermissions();
    return () => { isMounted = false; };
  }, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Nome deve conter apenas letras minúsculas, números e underscore';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Nome de exibição é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onRoleAdded(formData);
    } catch (error) {
      setErrors({ submit: error?.message || 'Erro ao criar função. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  // Agrupar permissões por categoria (API)
  const permissionsByCategory = useMemo(() => {
    return permissionsByModule || {};
  }, [permissionsByModule]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Nova Função</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {errors.submit && <div className="error-message">{errors.submit}</div>}
            
            <div className="form-group">
              <label htmlFor="name">Nome da Função *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
                placeholder="ex: desenvolvedor"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="display_name">Nome de Exibição *</label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className={errors.display_name ? 'error' : ''}
                placeholder="ex: Desenvolvedor"
              />
              {errors.display_name && <span className="error-message">{errors.display_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descrição da função..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Permissões</label>
              <div className="permissions-grid">
                {loadingData ? (
                  <div className="loading">Carregando permissões...</div>
                ) : Object.keys(permissionsByCategory || {}).length === 0 ? (
                  <div className="empty">Nenhuma permissão disponível</div>
                ) : (
                  Object.entries(permissionsByCategory).map(([category, permissions]) => (
                    <div key={category} className="permission-category">
                      <h4 className="category-title">{category}</h4>
                      <div className="permission-list">
                        {permissions.map(permission => (
                          <label key={permission.id} className="permission-item">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                            />
                            <span className="permission-label">{permission.display_name || permission.name || permission.action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Função'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Editar Role
const EditRoleModal = ({ role, onClose, onRoleUpdated }) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    display_name: role?.display_name || '',
    description: role?.description || '',
    permissions: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [permissionsByModule, setPermissionsByModule] = useState({});

  // Carregar permissões disponíveis e dados atualizados da função
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        setLoadingData(true);
        const token = localStorage.getItem('token');

        // Buscar todas as permissões agrupadas por módulo
        const permsResp = await fetch(getApiUrl('roles/permissions/all'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const permsByModule = permsResp.ok ? await permsResp.json() : {};

        // Buscar dados do papel incluindo permissões com IDs
        const roleResp = await fetch(getApiUrl(`roles/${role?.id}`), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const roleData = roleResp.ok ? await roleResp.json() : null;

        if (!isMounted) return;

        setPermissionsByModule(permsByModule || {});

        if (roleData && Array.isArray(roleData.permissions)) {
          const selectedIds = roleData.permissions.map(p => p.id).filter(Boolean);
          setFormData(prev => ({ ...prev, permissions: selectedIds }));
        }
      } catch (err) {
        console.error('Erro ao carregar dados de função/permissões:', err);
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };

    if (role?.id) {
      loadData();
    }
    return () => { isMounted = false; };
  }, [role?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Nome de exibição é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Enviar apenas campos permitidos pelo backend (evitar 'name' em PUT)
      const payload = {
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions
      };
      await onRoleUpdated(payload);
    } catch (error) {
      setErrors({ submit: 'Erro ao atualizar função. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  // Agrupar permissões por categoria
  const permissionsByCategory = useMemo(() => {
    return permissionsByModule || {};
  }, [permissionsByModule]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Editar Função</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {errors.submit && <div className="error-message">{errors.submit}</div>}
            
            <div className="form-group">
              <label htmlFor="name">Nome da Função</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                disabled
                className="disabled"
              />
              <small className="form-help">O nome da função não pode ser alterado</small>
            </div>

            <div className="form-group">
              <label htmlFor="display_name">Nome de Exibição *</label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className={errors.display_name ? 'error' : ''}
                placeholder="ex: Desenvolvedor"
              />
              {errors.display_name && <span className="error-message">{errors.display_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descrição da função..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Permissões</label>
              <div className="permissions-grid">
                {loadingData ? (
                  <div className="loading">Carregando permissões...</div>
                ) : Object.keys(permissionsByCategory || {}).length === 0 ? (
                  <div className="empty">Nenhuma permissão disponível</div>
                ) : (
                  Object.entries(permissionsByCategory).map(([category, permissions]) => (
                    <div key={category} className="permission-category">
                      <h4 className="category-title">{category}</h4>
                      <div className="permission-list">
                        {permissions.map(permission => (
                          <label key={permission.id} className="permission-item">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                            />
                            <span className="permission-label">{permission.display_name || permission.name || permission.action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para Deletar Role
const DeleteRoleModal = ({ role, onClose, onRoleDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    
    try {
      await onRoleDeleted(role.id);
    } catch (error) {
      setError('Erro ao excluir função. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Excluir Função</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <p>
              Tem certeza que deseja excluir a função <strong>"{role?.display_name}"</strong>?
            </p>
            <p className="warning-text">
              Esta ação não pode ser desfeita. Todos os usuários com esta função perderão suas permissões associadas.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Excluindo...' : 'Excluir Função'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
